import { useEffect, useState, useCallback, useRef } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import {
  Plane,
  Waves,
  Droplets,
  Play,
  Square,
  Activity,
  Cpu,
  Gauge,
  Timer,
  AlertTriangle,
  Crosshair,
  Sparkles,
} from 'lucide-react';

const DOMAIN_ICON = { air: Plane, surface: Waves, underwater: Droplets };
const DOMAIN_COLOR = { air: 'text-sky-400', surface: 'text-emerald-400', underwater: 'text-blue-400' };

function Stat({ icon: Icon, label, value, accent = 'sky', sub }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-3">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
        <Icon size={11} /> {label}
      </div>
      <div className={`text-${accent}-300 text-2xl font-mono leading-none`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-zinc-500 font-mono mt-1">{sub}</div>}
    </div>
  );
}

export default function SaturationDrill() {
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState({ n_threats: 35, duration_s: 120, tick_hz: 4 });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/drills/saturation/status');
      setStatus(data);
      setNowMs(Date.now());
    } catch (err) {
      console.warn('saturation status poll failed:', err.message);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(fetchStatus);
    pollRef.current = setInterval(fetchStatus, 1500);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const start = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post('/drills/saturation/start', config);
      if (data.ok) toast.success(`Saturation drill started · ${config.n_threats} threats / ${config.duration_s}s`);
      else toast.error(data.reason || 'Failed to start');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Start failed');
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async () => {
    setSubmitting(true);
    try {
      await axios.post('/drills/saturation/stop');
      toast.success('Saturation drill stopped');
      fetchStatus();
    } catch (e) {
      toast.error('Stop failed');
    } finally {
      setSubmitting(false);
    }
  };

  const running = status?.running;
  const stats = status?.stats || {};
  const fusion = status?.fusion || {};
  const threats = status?.threats || [];
  const fusionStats = fusion.stats || {};
  const cps = stats.ticks ? Math.round((stats.contacts_injected || 0) / Math.max(1, stats.ticks)) : 0;
  const startedAt = status?.started_at ? new Date(status.started_at) : null;
  const elapsedS = startedAt && running ? Math.floor((nowMs - startedAt.getTime()) / 1000) : 0;

  // SLA gates for the live-fire trial
  const tickHealthy = (stats.tick_p95_ms || 0) < 100;
  const trackerKeepingUp =
    (fusionStats.live_tracks || 0) >= Math.min(threats.length, 20) || !running;
  const noErrors = (stats.errors || 0) === 0;
  const goNoGo = tickHealthy && trackerKeepingUp && noErrors && (stats.contacts_injected || 0) > 0;

  return (
    <div className="flex flex-col h-full bg-zinc-950" data-testid="saturation-drill-page">
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <Sparkles size={20} className="text-amber-400" />
              Saturation Dry-Run
            </h1>
            <p className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mt-0.5">
              Pre-Trial Load Test · Pushes synthetic threats through real HAL → Fusion → Engagement path
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!running && (
              <button
                onClick={start}
                disabled={submitting}
                className="px-4 py-2 bg-amber-500/20 border border-amber-500/60 hover:bg-amber-500/30 text-amber-200 rounded-sm flex items-center gap-2 font-mono uppercase tracking-widest text-xs disabled:opacity-50"
                data-testid="drill-start-button"
              >
                <Play size={14} /> Start Drill
              </button>
            )}
            {running && (
              <button
                onClick={stop}
                disabled={submitting}
                className="px-4 py-2 bg-red-500/20 border border-red-500/60 hover:bg-red-500/30 text-red-200 rounded-sm flex items-center gap-2 font-mono uppercase tracking-widest text-xs disabled:opacity-50"
                data-testid="drill-stop-button"
              >
                <Square size={14} /> Stop
              </button>
            )}
          </div>
        </div>

        {/* Config row (disabled while running) */}
        <div className="flex items-center gap-4 text-[11px] font-mono">
          {[
            { key: 'n_threats', label: 'Threats', min: 1, max: 100, step: 1 },
            { key: 'duration_s', label: 'Duration (s)', min: 10, max: 600, step: 10 },
            { key: 'tick_hz', label: 'Tick rate (Hz)', min: 1, max: 20, step: 1 },
          ].map((f) => (
            <label key={f.key} className="flex items-center gap-2">
              <span className="text-zinc-500 uppercase tracking-widest text-[9px]">{f.label}</span>
              <input
                type="number"
                min={f.min}
                max={f.max}
                step={f.step}
                value={config[f.key]}
                disabled={running}
                onChange={(e) => setConfig({ ...config, [f.key]: Number(e.target.value) })}
                className="w-20 bg-zinc-900 border border-zinc-700 px-2 py-1 text-zinc-200 rounded-sm disabled:opacity-40"
                data-testid={`config-${f.key}`}
              />
            </label>
          ))}
          {running && (
            <span className="ml-auto flex items-center gap-1.5 text-emerald-300">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              LIVE · elapsed {elapsedS}s / {status.config?.duration_s}s
            </span>
          )}
        </div>
      </header>

      {/* GO/NO-GO band */}
      <div
        className={`border-b px-6 py-3 flex items-center justify-between ${
          !running ? 'border-zinc-800 bg-zinc-900/40' : goNoGo ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'
        }`}
        data-testid="drill-banner"
      >
        <div className="flex items-center gap-3">
          {!running && <Crosshair size={20} className="text-zinc-500" />}
          {running && goNoGo && <Crosshair size={20} className="text-emerald-400" />}
          {running && !goNoGo && <AlertTriangle size={20} className="text-red-400" />}
          <div>
            <div className={`text-lg font-bold tracking-tight ${!running ? 'text-zinc-400' : goNoGo ? 'text-emerald-300' : 'text-red-300'}`}>
              {!running ? 'IDLE' : goNoGo ? 'PIPELINE NOMINAL · GO' : 'PIPELINE DEGRADED · INVESTIGATE'}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              SLA: tick p95 &lt; 100 ms · errors = 0 · fusion keeps up with spawned threats
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className={tickHealthy ? 'text-emerald-300' : 'text-red-300'}>
            tick p95 {stats.tick_p95_ms ?? 0}ms
          </span>
          <span className={noErrors ? 'text-emerald-300' : 'text-red-300'}>
            err {stats.errors ?? 0}
          </span>
          <span className={trackerKeepingUp ? 'text-emerald-300' : 'text-red-300'}>
            tracks {fusionStats.live_tracks ?? 0}/{threats.length || stats.threats_spawned || 0}
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="p-6 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Stat icon={Crosshair} label="Threats Spawned" value={stats.threats_spawned ?? 0} accent="amber" />
        <Stat icon={Activity} label="Contacts Injected" value={stats.contacts_injected ?? 0} accent="sky" sub={`${cps}/tick`} />
        <Stat icon={Timer} label="Ticks" value={stats.ticks ?? 0} accent="zinc" />
        <Stat icon={Gauge} label="Tick p95" value={`${stats.tick_p95_ms ?? 0}ms`} accent={tickHealthy ? 'emerald' : 'red'} />
        <Stat icon={Cpu} label="Live Tracks" value={fusionStats.live_tracks ?? 0} accent="emerald" />
        <Stat icon={Sparkles} label="Associations" value={fusionStats.associations ?? 0} accent="amber" />
        <Stat icon={AlertTriangle} label="Errors" value={stats.errors ?? 0} accent={noErrors ? 'zinc' : 'red'} />
      </div>

      {/* Threat table */}
      <div className="flex-1 px-6 pb-6 overflow-hidden flex flex-col">
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm flex flex-col overflow-hidden flex-1">
          <div className="px-3 py-2 border-b border-zinc-800 text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Crosshair size={12} className="text-amber-400" /> Synthetic Threats ({threats.length})
            </span>
            <span className="text-zinc-500 text-[10px]">
              air {threats.filter((t) => t.domain === 'air').length} ·
              surface {threats.filter((t) => t.domain === 'surface').length} ·
              underwater {threats.filter((t) => t.domain === 'underwater').length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="threat-table">
            {threats.length === 0 && (
              <div className="px-3 py-10 text-center text-zinc-600 font-mono text-sm">
                No synthetic threats — start a drill to load the pipeline.
              </div>
            )}
            {threats.length > 0 && (
              <table className="w-full text-[11px] font-mono">
                <thead className="bg-zinc-900/80 text-[9px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">ID</th>
                    <th className="text-left px-3 py-2">Domain</th>
                    <th className="text-left px-3 py-2">Class</th>
                    <th className="text-right px-3 py-2">Lat</th>
                    <th className="text-right px-3 py-2">Lng</th>
                    <th className="text-right px-3 py-2">Alt (m)</th>
                    <th className="text-right px-3 py-2">Speed (km/h)</th>
                    <th className="text-right px-3 py-2">Bearing</th>
                    <th className="text-right px-3 py-2">Age (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {threats.map((t) => {
                    const Icon = DOMAIN_ICON[t.domain] || Plane;
                    return (
                      <tr key={t.id} className="border-t border-zinc-900 hover:bg-zinc-800/40">
                        <td className="px-3 py-1.5 text-zinc-200">{t.id}</td>
                        <td className="px-3 py-1.5 flex items-center gap-1.5">
                          <Icon size={11} className={DOMAIN_COLOR[t.domain]} />
                          <span className={DOMAIN_COLOR[t.domain]}>{t.domain}</span>
                        </td>
                        <td className="px-3 py-1.5 text-zinc-400">{t.classification}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-400">{t.lat.toFixed(4)}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-400">{t.lng.toFixed(4)}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-400">{t.altitude.toFixed(0)}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-300">{(t.speed_mps * 3.6).toFixed(0)}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-400">{t.bearing_deg.toFixed(0)}°</td>
                        <td className="px-3 py-1.5 text-right text-zinc-500">{t.age_s}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
